// src/pages/ContactPage.tsx
import React, { useState } from "react";
import { collection, addDoc } from "firebase/firestore";
import { db } from "@/firebase/firebase";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useRecaptcha } from "@/hooks/useRecaptcha";
import { useToast } from "@/hooks/use-toast";
import "./styles/ContactPage.css";

interface RecaptchaVerificationRequest {
  token: string;
  action: string;
}
interface RecaptchaVerificationResponse {
  success: boolean;
  score: number;
  action: string;
}

const functions = getFunctions();
const verifyRecaptcha = httpsCallable<
  RecaptchaVerificationRequest,
  RecaptchaVerificationResponse
>(functions, "verifyRecaptcha");

const RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY;

export default function ContactPage() {
  const { toasts, toast } = useToast();
  const { isReady, execute, reset } = useRecaptcha(RECAPTCHA_SITE_KEY);

  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  function handleChange(
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const { name, email, message } = form;

    if (!name || !email || !message) {
      toast({ title: "All fields are required", variant: "destructive" });
      return;
    }
    if (!isReady) {
      toast({ title: "reCAPTCHA not ready", variant: "destructive" });
      return;
    }

    let token = "";
    try {
      const result = await execute("contact");
      if (!result) {
        toast({ title: "reCAPTCHA error", variant: "destructive" });
        return;
      }
      token = result;
    } catch {
      toast({ title: "reCAPTCHA error", variant: "destructive" });
      return;
    }

    let rc: RecaptchaVerificationResponse;
    try {
      const res = await verifyRecaptcha({ token, action: "contact" });
      rc = res.data;
    } catch {
      toast({ title: "reCAPTCHA verification failed", variant: "destructive" });
      reset();
      return;
    }

    if (!rc.success || rc.score < 0.5) {
      toast({ title: "Failed reCAPTCHA check", variant: "destructive" });
      reset();
      return;
    }

    setLoading(true);
    try {
      await addDoc(collection(db, "contacts"), {
        name,
        email,
        message,
        createdAt: new Date().toISOString(),
        recaptchaScore: rc.score,
        recaptchaAction: rc.action,
      });
      toast({ title: "Message sent!", description: "We'll be in touch soon." });
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast({
        title: "Error sending message",
        description: "Please try again later.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="contact-page">
      <header className="contact-hero">
        <h1>📨 Get in Touch</h1>
        <p>Questions? Feedback? Drop us a line below.</p>
      </header>

      <div className="contact-container">
        <form className="contact-form" onSubmit={handleSubmit}>
          <label>
            Name
            <input
              name="name"
              value={form.name}
              onChange={handleChange}
              autoComplete="name"
              required
            />
          </label>

          <label>
            Email
            <input
              name="email"
              type="email"
              value={form.email}
              onChange={handleChange}
              autoComplete="email"
              required
            />
          </label>

          <label>
            Message
            <textarea
              name="message"
              rows={5}
              value={form.message}
              onChange={handleChange}
              autoComplete="off"
              required
            />
          </label>

          <button type="submit" disabled={loading}>
            {loading ? "Sending…" : "Send Message"}
          </button>
        </form>
      </div>

      <div className="toast-wrapper">
        {toasts.map((t) => (
          <div key={t.id} className={`toast toast-${t.variant ?? "default"}`}>
            <strong>{t.title}</strong>
            {t.description && <p>{t.description}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}
